import React, { useEffect, useState } from "react";
import axios from "axios";
import { Container, Table, Button, Row, Col, Form, Modal, Spinner, Card, Badge, Nav, Tab } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_ENDPOINTS } from "../../../config/api";
import '../Dashboard.css';

const AccountConfirmation = () => {
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [confirmedAccounts, setConfirmedAccounts] = useState([]);
  const [declinedAccounts, setDeclinedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("decline"); // "decline", "approve", "revoke"
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    // Fetch pending, confirmed, and declined accounts from the backend
    fetchPendingAccounts();
    fetchConfirmedAccounts();
    fetchDeclinedAccounts();
  }, [refreshTrigger]);

  const fetchPendingAccounts = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(`${API_ENDPOINTS.ADMIN.BASE}/pending-accounts`);
      
      setPendingAccounts(response.data);
      setLoading(false);
    } catch (error) {
      // console.error("Error fetching pending accounts:", error);
      toast.error("Failed to fetch pending accounts.");
      setLoading(false);
    }
  };

  const fetchConfirmedAccounts = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.ADMIN.BASE}/confirmed-accounts`);
     
      setConfirmedAccounts(response.data);
    } catch (error) {
      // console.error("Error fetching confirmed accounts:", error);
      toast.error("Failed to fetch confirmed accounts.");
    }
  };

  const fetchDeclinedAccounts = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.ADMIN.BASE}/declined-accounts`);
      
      setDeclinedAccounts(response.data);
    } catch (error) {
      // console.error("Error fetching declined accounts:", error);
      toast.error("Failed to fetch declined accounts.");
    }
  };

  const handleApproval = async (userId) => {
    try {
      await axios.post(`${API_ENDPOINTS.ADMIN.BASE}/account-action`, {
        userId,
        action: "approved",
      });
      toast.success("Account approved successfully!");
      // Trigger a refresh of all account lists
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // console.error("Error approving account:", error);
      toast.error("Failed to approve account.");
    }
  };

  const openDeclineModal = (account) => {
    setSelectedAccount(account);
    setFeedbackMessage("");
    setModalType("decline");
    setShowModal(true);
  };

  const openRevokeApprovalModal = (account) => {
    setSelectedAccount(account);
    setFeedbackMessage("");
    setModalType("revoke");
    setShowModal(true);
  };

  const openApproveDeclinedModal = (account) => {
    setSelectedAccount(account);
    setModalType("approve");
    setShowModal(true);
  };

  const handleModalAction = async () => {
    if (!selectedAccount) return;

    try {
      let action;
      let successMessage;
      
      switch (modalType) {
        case "decline":
          action = "disapproved";
          successMessage = "Account declined successfully!";
          break;
        case "revoke":
          action = "disapproved";
          successMessage = "Approval revoked successfully!";
          break;
        case "approve":
          action = "approved";
          successMessage = "Account approved successfully!";
          break;
        default:
          return;
      }

      await axios.post(`${API_ENDPOINTS.ADMIN.BASE}/account-action`, {
        userId: selectedAccount.id,
        action: action,
        feedback: modalType === "decline" || modalType === "revoke" ? feedbackMessage : "",
      });
      
      toast.success(successMessage);
      setShowModal(false);
      // Trigger a refresh of all account lists
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // console.error("Error updating account status:", error);
      toast.error("Failed to update account status.");
    }
  };

  return (
    <div className="main-content">
      <Container fluid className="px-4 py-3">
        <ToastContainer />
        
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-0" style={{ color: '#a70a4a', fontWeight: '600' }}>Account Management</h2>
            <p style={{ color: '#7f8c9b', fontSize: '0.85rem', marginBottom: 0 }}>Review and manage volunteer account statuses</p>
          </div>
          <button
            onClick={() => setRefreshTrigger(prev => prev + 1)}
            className="db1-export-btn"
            style={{ background: 'transparent', color: '#ac2e62', border: '1px solid rgba(172,46,98,0.3)' }}
          >
            <i className="bi bi-arrow-clockwise me-2"></i> Refresh
          </button>
        </div>

        <div className="db1-glass-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ borderBottom: '1px solid rgba(0,0,0,0.04)', padding: '0.5rem 1.25rem 0' }}>
            <ul className="nav" style={{ gap: '4px' }}>
              {['pending', 'approved', 'declined'].map(tab => (
                <li className="nav-item" key={tab}>
                  <button
                    onClick={() => setActiveTab(tab)}
                    className={`db1-tab-btn ${activeTab === tab ? 'active' : ''}`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    {(tab === 'pending' && pendingAccounts.length > 0) && (
                      <span className="db1-badge active ms-2">{pendingAccounts.length}</span>
                    )}
                    {(tab === 'approved' && confirmedAccounts.length > 0) && (
                      <span className="db1-badge active ms-2">{confirmedAccounts.length}</span>
                    )}
                    {(tab === 'declined' && declinedAccounts.length > 0) && (
                      <span className="db1-badge inactive ms-2">{declinedAccounts.length}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Pending Accounts Tab */}
          {activeTab === "pending" && (
            <div style={{ padding: '1.25rem' }}>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" style={{ color: '#ac2e62' }} />
                  <p className="mt-3" style={{ color: '#7f8c9b' }}>Loading pending accounts...</p>
                </div>
              ) : pendingAccounts.length === 0 ? (
                <div className="text-center py-5">
                  <h4 style={{ color: '#2c3e50' }}>No pending account requests</h4>
                  <p style={{ color: '#7f8c9b' }}>All account requests have been processed</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0 align-middle">
                    <thead>
                      <tr>
                        <th className="ps-4" style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Name</th>
                        <th style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Email</th>
                        <th style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Registration Date</th>
                        <th className="text-end pe-4" style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingAccounts.map((account) => (
                        <tr key={account.id}>
                          <td className="ps-4" style={{ padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                            <div className="d-flex align-items-center gap-3">
                              <div className="db1-avatar" style={{ width: 36, height: 36, fontSize: '0.85rem', borderRadius: '10px' }}>
                                {account.name.charAt(0).toUpperCase()}
                              </div>
                              <span style={{ color: '#2c3e50' }}>{account.name}</span>
                            </div>
                          </td>
                          <td style={{ color: '#2c3e50', padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>{account.email}</td>
                          <td style={{ color: '#7f8c9b', padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                            {account.createdAt ? new Date(account.createdAt).toLocaleDateString() : "N/A"}
                          </td>
                          <td className="text-end pe-4" style={{ padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                            <div className="d-flex justify-content-end gap-2">
                              <button
                                onClick={() => handleApproval(account.id)}
                                className="db1-export-btn"
                                style={{ background: '#16a34a', color: 'white', padding: '6px 14px', fontSize: '0.82rem' }}
                              >
                                <i className="bi bi-check-circle me-1"></i> Approve
                              </button>
                              <button
                                onClick={() => openDeclineModal(account)}
                                className="db1-delete-btn"
                              >
                                <i className="bi bi-x-circle me-1"></i> Decline
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Approved Accounts Tab */}
          {activeTab === "approved" && (
            <div style={{ padding: '1.25rem' }}>
              {confirmedAccounts.length === 0 ? (
                <div className="text-center py-5">
                  <h4 style={{ color: '#2c3e50' }}>No approved accounts</h4>
                  <p style={{ color: '#7f8c9b' }}>No accounts have been approved yet</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0 align-middle">
                    <thead>
                      <tr>
                        <th className="ps-4" style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Name</th>
                        <th style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Email</th>
                        <th style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Status</th>
                        <th className="text-end pe-4" style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {confirmedAccounts.map((account) => (
                        <tr key={account.id}>
                          <td className="ps-4" style={{ padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                            <div className="d-flex align-items-center gap-3">
                              <div className="db1-avatar" style={{ width: 36, height: 36, fontSize: '0.85rem', borderRadius: '10px' }}>
                                {account.name.charAt(0).toUpperCase()}
                              </div>
                              <span style={{ color: '#2c3e50' }}>{account.name}</span>
                            </div>
                          </td>
                          <td style={{ color: '#2c3e50', padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>{account.email}</td>
                          <td style={{ padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                            <span className="db1-badge active">Approved</span>
                          </td>
                          <td className="text-end pe-4" style={{ padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                            <button
                              onClick={() => openRevokeApprovalModal(account)}
                              className="db1-export-btn"
                              style={{ background: 'rgba(255,193,7,0.15)', color: '#cc9a06', padding: '6px 14px', fontSize: '0.82rem' }}
                            >
                              <i className="bi bi-arrow-counterclockwise me-1"></i> Revoke
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Declined Accounts Tab */}
          {activeTab === "declined" && (
            <div style={{ padding: '1.25rem' }}>
              {declinedAccounts.length === 0 ? (
                <div className="text-center py-5">
                  <h4 style={{ color: '#2c3e50' }}>No declined accounts</h4>
                  <p style={{ color: '#7f8c9b' }}>No accounts have been declined yet</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0 align-middle">
                    <thead>
                      <tr>
                        <th className="ps-4" style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Name</th>
                        <th style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Email</th>
                        <th style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Feedback</th>
                        <th className="text-end pe-4" style={{ color: '#7f8c9b', fontWeight: 600, fontSize: '0.82rem', borderBottom: '2px solid rgba(172,46,98,0.1)' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {declinedAccounts.map((account) => (
                        <tr key={account.id}>
                          <td className="ps-4" style={{ padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                            <div className="d-flex align-items-center gap-3">
                              <div className="db1-avatar" style={{ width: 36, height: 36, fontSize: '0.85rem', borderRadius: '10px' }}>
                                {account.name.charAt(0).toUpperCase()}
                              </div>
                              <span style={{ color: '#2c3e50' }}>{account.name}</span>
                            </div>
                          </td>
                          <td style={{ color: '#2c3e50', padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>{account.email}</td>
                          <td style={{ color: '#7f8c9b', padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>{account.feedback || "No feedback provided"}</td>
                          <td className="text-end pe-4" style={{ padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                            <button
                              onClick={() => openApproveDeclinedModal(account)}
                              className="db1-export-btn"
                              style={{ background: '#16a34a', color: 'white', padding: '6px 14px', fontSize: '0.82rem' }}
                            >
                              <i className="bi bi-check-circle me-1"></i> Approve
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <div className="db1-modal-header">
            <h5 className="mb-0" style={{ color: '#2c3e50' }}>
              {modalType === "decline" && "Decline Account"}
              {modalType === "revoke" && "Revoke Approval"}
              {modalType === "approve" && "Approve Account"}
            </h5>
            <button onClick={() => setShowModal(false)} className="btn-close" style={{ color: '#a70a4a' }}></button>
          </div>
          <div className="db1-modal-content">
            {selectedAccount && (
              <>
                <p style={{ color: '#2c3e50' }}>
                  {modalType === "decline" && "Are you sure you want to decline the account for:"}
                  {modalType === "revoke" && "Are you sure you want to revoke approval for:"}
                  {modalType === "approve" && "Are you sure you want to approve the account for:"}
                </p>
                
                <div className="db1-glass-card" style={{ padding: '12px 16px', marginBottom: '16px' }}>
                  <div className="d-flex align-items-center gap-3">
                    <div className={`db1-avatar`} style={{ width: 40, height: 40, fontSize: '1rem', borderRadius: '12px' }}>
                      {selectedAccount.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h6 className="mb-0" style={{ color: '#2c3e50' }}>{selectedAccount.name}</h6>
                      <small style={{ color: '#7f8c9b' }}>{selectedAccount.email}</small>
                    </div>
                  </div>
                </div>
                
                {(modalType === "decline" || modalType === "revoke") && (
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#2c3e50', fontWeight: 600 }}>
                      Feedback Message {modalType === "decline" ? "(Optional)" : ""}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                      placeholder={
                        modalType === "decline" 
                          ? "Provide feedback to the user about why their account was declined" 
                          : "Provide feedback about why approval is being revoked"
                      }
                    />
                    <Form.Text className="text-muted">
                      This message will be displayed to the user when they attempt to log in.
                    </Form.Text>
                  </Form.Group>
                )}
              </>
            )}
          </div>
          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid rgba(0,0,0,0.04)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button onClick={() => setShowModal(false)} className="db1-delete-btn">Cancel</button>
            <button
              onClick={handleModalAction}
              className="db1-export-btn"
              style={{
                background: modalType === "approve" ? '#16a34a' : '#dc2626',
                color: 'white'
              }}
            >
              {modalType === "decline" && "Decline Account"}
              {modalType === "revoke" && "Revoke Approval"}
              {modalType === "approve" && "Approve Account"}
            </button>
          </div>
        </Modal>
      </Container>
    </div>
  );
};

export default AccountConfirmation;